import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, ArrowRight, Eye, EyeOff, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { handleApiError } from "@/lib/api";
import { getLandingUrl } from "@/lib/landingUrl";

const KREDZ_LOGO = null;

const loginErrorMessage = (err) => {
  const status = err?.response?.status;
  if (status === 401) return "Email ou senha incorretos. Verifique os dados e tente novamente.";
  if (status === 429) return "Muitas tentativas de login. Aguarde alguns minutos e tente novamente.";
  if (!err?.response || status >= 500) return "Não foi possível entrar. Tente novamente em instantes.";
  return handleApiError(err, "Não foi possível entrar. Tente novamente em instantes.");
};

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [cognitoSession, setCognitoSession] = useState(null);
  const [cognitoEmail, setCognitoEmail] = useState("");
  const [accountSelection, setAccountSelection] = useState(null);
  const [newPasswordRequired, setNewPasswordRequired] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const { login, loginMfa, selectAccount } = useAuth();
  const navigate = useNavigate();

  const navigateByRole = (data) => {
    if (data.mfa_setup_required) {
      toast.info("Configure a autenticação de dois fatores (2FA) para acessar a plataforma.");
      navigate("/company/dashboard");
      return;
    }
    toast.success(`Bem-vindo, ${data.user.name}!`);
    const routes = { employee: "/employee", company_admin: "/company/dashboard", admin: "/admin/dashboard" };
    navigate(routes[data.user.role] || "/");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.mfa_required) {
        setMfaRequired(true);
        setMfaToken(data.mfa_token);
        if (data.cognito_session) {
          setCognitoSession(data.cognito_session);
          setCognitoEmail(email);
        }
        setLoading(false);
        return;
      }
      if (data.new_password_required) {
        setCognitoSession(data.cognito_session);
        setCognitoEmail(email);
        setNewPasswordRequired(true);
        setLoading(false);
        return;
      }
      if (data.account_selection) {
        setAccountSelection(data);
        setLoading(false);
        return;
      }
      navigateByRole(data);
    } catch (err) {
      toast.error(loginErrorMessage(err), {
        duration: 6000,
      });
    }
    setLoading(false);
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await loginMfa(mfaToken, mfaCode, cognitoSession, cognitoEmail);
      if (data.account_selection) {
        setAccountSelection(data);
        setMfaRequired(false);
        setLoading(false);
        return;
      }
      navigateByRole(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Código MFA inválido");
    }
    setLoading(false);
  };

  const handleSelectAccount = async (accountId) => {
    setLoading(true);
    try {
      const data = await selectAccount(accountSelection.user_id, accountId, password, accountSelection.selection_token);
      navigateByRole(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao selecionar conta");
    }
    setLoading(false);
  };

  const handleNewPasswordSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== newPasswordConfirm) {
      toast.error("As senhas não coincidem");
      return;
    }
    if (newPassword.length < 12) {
      toast.error("Senha deve ter no mínimo 12 caracteres");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/auth/cognito/new-password", {
        email: cognitoEmail,
        session: cognitoSession,
        new_password: newPassword,
      });
      if (res.data.token || res.data.cognito_token) {
        const primaryToken = res.data.cognito_token || res.data.token;
        localStorage.setItem("token", primaryToken);
        if (res.data.refresh_token) localStorage.setItem("refresh_token", res.data.refresh_token);
        if (res.data.cognito_refresh_token) {
          localStorage.setItem("cognito_refresh_token", res.data.cognito_refresh_token);
          localStorage.setItem("auth_provider", "cognito");
        }
        toast.success("Senha definida com sucesso!");
        navigate("/company/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao definir nova senha");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="/images/login-bg.jpg"
          alt="bg" className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a12]/70 to-transparent" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <a
            href={getLandingUrl()}
            className="flex items-center gap-3 mb-6 group transition-opacity hover:opacity-80"
            data-testid="login-logo-landing-link"
            aria-label="Ir para a página inicial Kredz"
          >
            <img src="/logo-kredz-dark.png" alt="Kredz" className="h-14 w-auto transition-transform group-hover:scale-105" />
          </a>
          <h1 className="text-4xl font-bold mb-3 tracking-tight">Antecipação de recebíveis simplificada</h1>
          <p className="text-lg text-white/70 max-w-md">Plataforma completa para gestão e antecipação de recebíveis empresariais.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md space-y-8 animate-fade-in">
          <a
            href={getLandingUrl()}
            className="lg:hidden flex items-center gap-3 mb-2 group transition-opacity hover:opacity-80"
            data-testid="login-logo-landing-link-mobile"
            aria-label="Ir para a página inicial Kredz"
          >
            <img src="/logo-kredz-dark.png" alt="Kredz" className="h-12 w-auto transition-transform group-hover:scale-105" />
          </a>

          <div>
            <h2 className="text-3xl font-bold tracking-tight">
              {accountSelection ? "Selecione o perfil" : newPasswordRequired ? "Defina sua nova senha" : "Entrar"}
            </h2>
            <p className="text-muted-foreground mt-2">
              {accountSelection ? "Você tem acesso a múltiplos perfis" : newPasswordRequired ? "Sua conta foi migrada. Defina uma nova senha para continuar." : "Acesse sua conta para continuar"}
            </p>
          </div>

          {newPasswordRequired ? (
            <form onSubmit={handleNewPasswordSubmit} className="space-y-5 animate-fade-in" data-testid="new-password-form">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/10">
                <Lock className="w-5 h-5 text-amber-400" />
                <p className="text-sm">Defina uma senha forte com no mínimo 12 caracteres, incluindo maiúsculas, minúsculas, números e símbolos.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password">Nova senha</Label>
                <Input id="new-password" data-testid="new-password-input" type="password"
                  value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required
                  placeholder="Mínimo 12 caracteres"
                  className="h-11 bg-black/20 border-white/10 focus:border-blue-500" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password-confirm">Confirmar senha</Label>
                <Input id="new-password-confirm" data-testid="new-password-confirm-input" type="password"
                  value={newPasswordConfirm} onChange={(e) => setNewPasswordConfirm(e.target.value)} required
                  placeholder="Repita a senha"
                  className="h-11 bg-black/20 border-white/10 focus:border-blue-500" />
              </div>
              <Button data-testid="new-password-submit-btn" type="submit" disabled={loading || newPassword.length < 12}
                className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                {loading ? <div className="loading-spinner" /> : <>Definir senha <ArrowRight className="w-4 h-4" /></>}
              </Button>
              <button type="button" onClick={() => { setNewPasswordRequired(false); setNewPassword(""); setNewPasswordConfirm(""); }}
                className="text-sm text-muted-foreground hover:text-foreground w-full text-center">Voltar ao login</button>
            </form>
          ) : accountSelection ? (
            <div className="space-y-3 animate-fade-in">
              {accountSelection.accounts.map((acc) => (
                <button key={acc.id} data-testid={`account-${acc.id}`}
                  onClick={() => handleSelectAccount(acc.id)}
                  disabled={loading}
                  className="w-full p-4 rounded-xl border border-white/10 bg-black/20 hover:bg-blue-500/10 hover:border-blue-500/30 transition-all text-left flex items-center gap-4 group">
                  <div className={`p-2.5 rounded-lg ${
                    acc.role === "admin" ? "bg-red-500/10" :
                    acc.role === "company_admin" ? "bg-emerald-500/10" : "bg-blue-500/10"
                  }`}>
                    <Shield className={`w-5 h-5 ${
                      acc.role === "admin" ? "text-red-400" :
                      acc.role === "company_admin" ? "text-emerald-400" : "text-blue-400"
                    }`} />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{acc.label}</p>
                    <p className="text-xs text-muted-foreground">{acc.role === "admin" ? "Administrador do sistema" : acc.role === "company_admin" ? "Gestão da empresa" : "Acesso de colaborador"}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 ml-auto text-muted-foreground group-hover:text-foreground transition-colors" />
                </button>
              ))}
              <button onClick={() => { setAccountSelection(null); setMfaRequired(false); setMfaCode(""); }}
                className="text-sm text-muted-foreground hover:text-foreground w-full text-center mt-2">Voltar ao login</button>
            </div>
          ) : (

          <form onSubmit={mfaRequired ? handleMfaSubmit : handleSubmit} className="space-y-6">
            {mfaRequired ? (
              <div className="space-y-5 animate-fade-in">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-500/5 border border-blue-500/10">
                  <Lock className="w-5 h-5 text-blue-400" />
                  <p className="text-sm">Insira o código do seu autenticador</p>
                </div>
                <div className="space-y-2">
                  <Label>Código de verificação</Label>
                  <Input data-testid="mfa-login-code-input" value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="000000" maxLength={6}
                    className="h-12 text-center text-xl tracking-[0.5em] font-mono bg-black/20 border-white/10 focus:border-blue-500" />
                </div>
                <Button data-testid="mfa-login-submit-btn" type="submit" disabled={loading || mfaCode.length !== 6}
                  className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                  {loading ? <div className="loading-spinner" /> : <>Verificar <ArrowRight className="w-4 h-4" /></>}
                </Button>
                <button type="button" onClick={() => { setMfaRequired(false); setMfaCode(""); }}
                  className="text-sm text-muted-foreground hover:text-foreground w-full text-center">Voltar ao login</button>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email" data-testid="login-email-input" type="email"
                    placeholder="seu@email.com" value={email}
                    onChange={(e) => setEmail(e.target.value)} required
                    className="h-11 bg-black/20 border-white/10 focus:border-blue-500"
                  />
                </div>

                <div className="space-y-2 mt-5">
                  <Label htmlFor="password">Senha</Label>
                  <div className="relative">
                    <Input
                      id="password" data-testid="login-password-input"
                      type={showPassword ? "text" : "password"} placeholder="Sua senha"
                      value={password} onChange={(e) => setPassword(e.target.value)} required
                      className="h-11 bg-black/20 border-white/10 focus:border-blue-500 pr-10"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      data-testid="login-toggle-password-button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button data-testid="login-submit-btn" type="submit" disabled={loading}
                  className="w-full h-11 mt-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                  {loading ? <div className="loading-spinner" /> : <>Entrar <ArrowRight className="w-4 h-4" /></>}
                </Button>
                <div className="text-center mt-3">
                  <Link to="/reset-password" className="text-sm text-muted-foreground hover:text-blue-400" data-testid="forgot-password-link">
                    Esqueci minha senha
                  </Link>
                </div>
              </>
            )}
          </form>
          )}

          <div className="text-center text-sm text-muted-foreground">
            Não tem conta?{" "}
            <Link to="/register" className="text-blue-400 hover:text-blue-300 font-medium" data-testid="register-link">
              Criar conta
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
